import { z } from 'zod';
import {
    ArticleStatus,
    ArticleAssignmentBucket,
    ArticleContentSchema,
    ArticleReaction,
} from '../../entities/article';
import { College } from '../../entities/user';

export const ModuleAssignmentSchema = z.object({
    moduleNumber: z.number(),
    bucket: z.nativeEnum(ArticleAssignmentBucket),
    order: z.number().int().min(0).default(0),
});

// Lightweight schema for list views (without content)
export const ArticleSummarySchema = z.object({
    _id: z.string(),
    title: z.string(),
    imageUrl: z.string().url().optional(),
    titleColor: z.string().optional(),
    shortDescription: z.string().optional(),
    status: z.nativeEnum(ArticleStatus),
    moduleAssignments: z.array(ModuleAssignmentSchema),
    college: z.nativeEnum(College).optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});

// Full schema with content (for detail views)
export const ArticleSchema = z.object({
    _id: z.string(),
    title: z.string(),
    imageUrl: z.string().url().optional(),
    titleColor: z.string().optional(),
    shortDescription: z.string().optional(),
    content: ArticleContentSchema.optional(),
    status: z.nativeEnum(ArticleStatus),
    moduleAssignments: z.array(ModuleAssignmentSchema),
    college: z.nativeEnum(College).optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});

export const ArticleResponseSchema = z.object({
    article: ArticleSchema,
});

export const ArticlesListResponseSchema = z.object({
    articles: z.array(ArticleSummarySchema),
});

// Schema for grouped articles by quiz (personalized view)
export const ArticlesByQuizSchema = z.object({
    quizNumber: z.number(),
    moduleName: z.string(),
    articles: z.array(ArticleSummarySchema),
});

export const PersonalizedArticlesResponseSchema = z.object({
    articlesByQuiz: z.array(ArticlesByQuizSchema),
});

// General articles grouped by module (mirrors the personalized grouped shape)
export const GeneralArticlesResponseSchema = z.object({
    articlesByModule: z.array(ArticlesByQuizSchema),
});

// Input schema for getArticles
export const GetArticlesForUserInputSchema = z.object({
    type: z.enum(['general', 'personalized']),
});

// Output schema for getArticles (union of general and personalized)
export const GetArticlesForUserOutputSchema = z.union([
    GeneralArticlesResponseSchema,
    PersonalizedArticlesResponseSchema,
]);

// Input schema for getArticleById
export const GetArticleByIdInputSchema = z.object({
    id: z.string(),
});

// Article interaction schemas
export const RecordArticleReadInputSchema = z.object({
    articleId: z.string(),
});

export const RecordArticleReactionInputSchema = z.object({
    articleId: z.string(),
    reaction: z
        .nativeEnum(ArticleReaction)
        .refine(
            (val) => val !== ArticleReaction.None,
            'Reaction must be liked or disliked',
        ),
});

export const GetArticleInteractionInputSchema = z.object({
    articleId: z.string(),
});

export const ArticleInteractionSchema = z.object({
    articleId: z.string(),
    readCount: z.number(),
    reaction: z.nativeEnum(ArticleReaction),
    favorited: z.boolean(),
    lastReadAt: z.string().optional(),
    reactedAt: z.string().optional(),
    favoritedAt: z.string().optional(),
});

export const ArticleInteractionResponseSchema = z.object({
    interaction: ArticleInteractionSchema.optional(),
});

// IDs of every article the authenticated user has read (readCount > 0)
export const ReadArticleIdsResponseSchema = z.object({
    readArticleIds: z.array(z.string()),
});

// Input for toggling an article's favorite (star) state
export const ToggleFavoriteInputSchema = z.object({
    articleId: z.string(),
    favorited: z.boolean(),
});

// Favorited articles grouped by module (mirrors the general grouped shape)
export const FavoriteArticlesResponseSchema = z.object({
    articlesByModule: z.array(ArticlesByQuizSchema),
});

// IDs of every article the authenticated user has favorited
export const FavoriteArticleIdsResponseSchema = z.object({
    favoriteArticleIds: z.array(z.string()),
});
